/**
 * SuperAdmin Settings Page
 * Settings page with logout, two-factor authentication, change password, and update profile
 */
import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Card, 
  Switch, 
  Button, 
  Modal, 
  message, 
  Space, 
  Typography, 
  Row, 
  Col,
  Form,
  Input,
  Divider
} from 'antd';
import { 
  LogoutOutlined, 
  SafetyOutlined, 
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  UserOutlined,
  LockOutlined,
  EditOutlined,
  MailOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { logout as rbacLogout, getCurrentUser } from '../utils/rbac';
import { apiService } from '../services/apiService';
import SuperAdminSidebar from '../components/superadmin/SuperAdminSidebar';
import SuperAdminHeader from '../components/superadmin/SuperAdminHeader';
import { useResponsive } from '../hooks/useResponsive';
import './SuperAdminSettings.css';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;

interface UserProfile {
  id?: number;
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role?: {
    id?: number;
    name?: string;
  };
  [key: string]: any;
}

const SuperAdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);
  const [changePasswordModalVisible, setChangePasswordModalVisible] = useState(false);
  const [updateProfileModalVisible, setUpdateProfileModalVisible] = useState(false);
  const [twoFactorSetupModalVisible, setTwoFactorSetupModalVisible] = useState(false);
  const [twoFactorVerifyModalVisible, setTwoFactorVerifyModalVisible] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const [updateProfileForm] = Form.useForm();
  const [twoFactorVerifyForm] = Form.useForm();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [, setProfileLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [secretKey, setSecretKey] = useState<string>('');

  const loadUserProfile = React.useCallback(async () => {
    try {
      setProfileLoading(true);
      const profile = await apiService.get<UserProfile>('/auth/me');
      setUserProfile(profile);
      // Pre-fill form with current user data
      if (profile) {
        updateProfileForm.setFieldsValue({
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          email: profile.email || '',
          username: profile.username || '',
        });
      }
    } catch (error: any) {
      console.error('Failed to load user profile:', error);
      message.error('Failed to load user profile');
    } finally {
      setProfileLoading(false);
    }
  }, [updateProfileForm]);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  // Populate form when modal opens
  useEffect(() => {
    if (updateProfileModalVisible && userProfile) {
      updateProfileForm.setFieldsValue({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        email: userProfile.email || '',
        username: userProfile.username || '',
      });
    }
  }, [updateProfileModalVisible, userProfile, updateProfileForm]);

  const handleMenuClick = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleLogout = () => {
    confirm({
      title: 'Are you sure you want to logout?',
      icon: <ExclamationCircleOutlined />,
      content: 'You will be redirected to the login page.',
      okText: 'Yes, Logout',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        // Clear authentication
        rbacLogout();
        localStorage.clear();
        
        // Redirect to SuperAdmin login
        navigate('/superadmin/login');
        message.success('Logged out successfully');
      },
      onCancel() {
        console.log('Logout cancelled');
      },
    });
  };

  const handleTwoFactorToggle = (checked: boolean) => {
    if (checked) {
      // Enable 2FA - Immediately generate and show QR code
      try {
        setLoading(true);
        // Call API to generate 2FA secret and QR code
        // For now, we'll generate a mock QR code URL and secret
        // In production, this should come from the backend
        const mockSecret = generateSecretKey();
        const mockQrCode = generateQRCodeUrl(mockSecret, userProfile?.email || 'superadmin@system.bt');
        
        setSecretKey(mockSecret);
        setQrCodeUrl(mockQrCode);
        
        // Show setup modal with QR code
        setTwoFactorSetupModalVisible(true);
      } catch (error: any) {
        message.error('Failed to set up 2FA. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // Disable 2FA
      confirm({
        title: 'Disable Two-Factor Authentication?',
        icon: <ExclamationCircleOutlined />,
        content: 'Disabling 2FA will reduce the security of your account. Are you sure you want to continue?',
        okText: 'Disable',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          setLoading(true);
          // Simulate API call
          setTimeout(() => {
            setTwoFactorEnabled(false);
            setLoading(false);
            message.success('Two-factor authentication disabled');
          }, 1000);
        },
      });
    }
  };

  const handleSetup2FA = () => {
    // Close setup modal and open verification modal
    setTwoFactorSetupModalVisible(false);
    setTwoFactorVerifyModalVisible(true);
  };

  const handleVerify2FA = async (values: any) => {
    try {
      setLoading(true);
      // Verify the code with backend
      // For now, simulate verification
      setTimeout(() => {
        setTwoFactorEnabled(true);
        setTwoFactorVerifyModalVisible(false);
        twoFactorVerifyForm.resetFields();
        setLoading(false);
        message.success('Two-factor authentication enabled successfully!');
      }, 1000);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate a mock secret key (in production, this comes from backend)
  const generateSecretKey = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return secret.match(/.{4}/g)?.join(' ') || secret;
  };

  // Helper function to generate QR code URL (in production, this comes from backend)
  const generateQRCodeUrl = (secret: string, email: string): string => {
    const issuer = 'STOCKM SuperAdmin';
    const accountName = email;
    const otpAuthUrl = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${secret.replace(/\s/g, '')}&issuer=${encodeURIComponent(issuer)}`;
    // Using a QR code API service (in production, generate QR code on backend)
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpAuthUrl)}`;
  };

  const handleChangePassword = async (values: any) => {
    try {
      setLoading(true);
      // Call API to change password
      await apiService.put('/auth/me/change-password', {
        current_password: values.currentPassword,
        new_password: values.newPassword,
      });
      message.success('Password changed successfully');
      setChangePasswordModalVisible(false);
      changePasswordForm.resetFields();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Failed to change password. Please check your current password.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (values: any) => {
    try {
      setLoading(true);
      // Call API to update profile - Superadmin cannot change email
      const updatedProfile = await apiService.put<UserProfile>('/auth/me', {
        first_name: values.first_name,
        last_name: values.last_name,
        username: values.username,
        // Email is not included - Superadmin email cannot be changed
      });
      setUserProfile(updatedProfile);
      message.success('Profile updated successfully');
      setUpdateProfileModalVisible(false);
      // Update RBAC user data
      const currentUser = getCurrentUser();
      if (currentUser && updatedProfile) {
        const fullName = updatedProfile.first_name && updatedProfile.last_name
          ? `${updatedProfile.first_name} ${updatedProfile.last_name}`
          : updatedProfile.email?.split('@')[0] || currentUser.name;
        // Update localStorage
        if (updatedProfile.email) {
          localStorage.setItem('userEmail', updatedProfile.email);
        }
        localStorage.setItem('userName', fullName);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout className={`superadmin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={{ minHeight: '100dvh', background: '#f0f2f5', display: 'flex', flexDirection: 'row', overflowX: 'hidden', maxWidth: '100vw' }}>
      <SuperAdminSidebar 
        collapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
        user={{ name: 'Super Admin', email: 'admin@system.bt' }}
      />
      <Layout style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`, maxWidth: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`, marginLeft: isMobile ? 0 : (sidebarCollapsed ? 80 : 280), transition: 'all 0.3s ease', height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <SuperAdminHeader 
          onMenuClick={handleMenuClick}
          sidebarCollapsed={sidebarCollapsed}
          isMobile={isMobile}
        />
        <Content
          className="main-content-full superadmin-content"
          style={{
            flex: 1,
            minWidth: 0,
            marginTop: isMobile ? 64 : 80,
            padding: isMobile ? 12 : 24,
            minHeight: isMobile ? 'calc(100dvh - 64px)' : 'calc(100dvh - 80px)',
            background: '#ffffff',
            overflowY: 'auto',
            overflowX: 'hidden',
            width: '100%',
          }}
        >
          <div className="superadmin-settings-container">
            <Title level={2} style={{ marginBottom: 24 }}>
              Settings
            </Title>

            {/* Profile Settings */}
            <Card 
              title={
                <Space>
                  <UserOutlined />
                  <span>Profile Settings</span>
                </Space>
              }
              className="settings-card"
              style={{ marginBottom: 24 }}
            >
              <Row gutter={[16, 24]}>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <div className="settings-item">
                    <div className="settings-item-content">
                      <div className="settings-item-header">
                        <div>
                          <Title level={5} style={{ margin: 0 }}>
                            Update Profile
                          </Title>
                          <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
                            Update your personal information including name, email, and username
                          </Paragraph>
                        </div>
                        <Button
                          type="primary"
                          icon={<EditOutlined />}
                          onClick={() => {
                            // Ensure form is populated with current data before opening
                            if (userProfile) {
                              updateProfileForm.setFieldsValue({
                                first_name: userProfile.first_name || '',
                                last_name: userProfile.last_name || '',
                                email: userProfile.email || '',
                                username: userProfile.username || '',
                              });
                            }
                            setUpdateProfileModalVisible(true);
                          }}
                          size="large"
                        >
                          Update Profile
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Security Settings */}
            <Card 
              title={
                <Space>
                  <SafetyOutlined />
                  <span>Security Settings</span>
                </Space>
              }
              className="settings-card"
              style={{ marginBottom: 24 }}
            >
              <Row gutter={[16, 24]}>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <div className="settings-item">
                    <div className="settings-item-content">
                      <div className="settings-item-header">
                        <div>
                          <Title level={5} style={{ margin: 0 }}>
                            Change Password
                          </Title>
                          <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
                            Update your password to keep your account secure
                          </Paragraph>
                        </div>
                        <Button
                          type="default"
                          icon={<LockOutlined />}
                          onClick={() => setChangePasswordModalVisible(true)}
                          size="large"
                        >
                          Change Password
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Divider style={{ margin: '16px 0' }} />
                  <div className="settings-item">
                    <div className="settings-item-content">
                      <div className="settings-item-header">
                        <Title level={5} style={{ margin: 0 }}>
                          Two-Factor Authentication (2FA)
                        </Title>
                        <Switch
                          checked={twoFactorEnabled}
                          onChange={handleTwoFactorToggle}
                          loading={loading}
                          checkedChildren={<CheckCircleOutlined />}
                        />
                      </div>
                      <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                        Add an extra layer of security to your account by requiring a verification code 
                        from your mobile device in addition to your password when logging in.
                      </Paragraph>
                      {twoFactorEnabled && (
                        <div style={{ marginTop: 12, padding: 12, background: '#f6ffed', borderRadius: 4, border: '1px solid #b7eb8f' }}>
                          <Space>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <Text type="success">Two-factor authentication is currently enabled</Text>
                          </Space>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Account Actions */}
            <Card 
              title="Account Actions"
              className="settings-card"
            >
              <Row gutter={[16, 24]}>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <div className="settings-item">
                    <div className="settings-item-content">
                      <div className="settings-item-header settings-logout-row">
                        <div>
                          <Title level={5} className="settings-logout-title" style={{ margin: 0 }}>
                            Logout
                          </Title>
                          <Paragraph className="settings-logout-desc" style={{ marginTop: 4, marginBottom: 0 }}>
                            Sign out of your SuperAdmin account
                          </Paragraph>
                        </div>
                        <Button
                          type="primary"
                          danger
                          icon={<LogoutOutlined />}
                          onClick={handleLogout}
                          size="large"
                          className="settings-logout-btn"
                        >
                          Logout
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Change Password Modal */}
            <Modal
              title={
                <Space>
                  <LockOutlined />
                  <span>Change Password</span>
                </Space>
              }
              open={changePasswordModalVisible}
              onCancel={() => {
                setChangePasswordModalVisible(false);
                changePasswordForm.resetFields();
              }}
              footer={null}
              width={500}
            >
              <Form
                form={changePasswordForm}
                layout="vertical"
                onFinish={handleChangePassword}
                autoComplete="off"
              >
                <Form.Item
                  name="currentPassword"
                  label="Current Password"
                  rules={[{ required: true, message: 'Please enter your current password' }]}
                >
                  <Input.Password
                    placeholder="Enter current password"
                    prefix={<LockOutlined />}
                    size="large"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item
                  name="newPassword"
                  label="New Password"
                  rules={[
                    { required: true, message: 'Please enter a new password' },
                    { min: 6, message: 'Password must be at least 6 characters' },
                  ]}
                >
                  <Input.Password
                    placeholder="Enter new password"
                    prefix={<LockOutlined />}
                    size="large"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label="Confirm New Password"
                  dependencies={['newPassword']}
                  rules={[
                    { required: true, message: 'Please confirm your new password' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('The two passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="Confirm new password"
                    prefix={<LockOutlined />}
                    size="large"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                  <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={() => {
                      setChangePasswordModalVisible(false);
                      changePasswordForm.resetFields();
                    }}>
                      Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Change Password
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Modal>

            {/* Update Profile Modal */}
            <Modal
              title={
                <Space>
                  <UserOutlined />
                  <span>Update Profile</span>
                </Space>
              }
              open={updateProfileModalVisible}
              onCancel={() => {
                setUpdateProfileModalVisible(false);
                updateProfileForm.resetFields();
              }}
              footer={null}
              width={600}
              destroyOnClose={false}
            >
              <Form
                form={updateProfileForm}
                layout="vertical"
                onFinish={handleUpdateProfile}
                autoComplete="off"
                preserve={true}
                initialValues={{
                  first_name: userProfile?.first_name || '',
                  last_name: userProfile?.last_name || '',
                  email: userProfile?.email || '',
                  username: userProfile?.username || '',
                }}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="first_name"
                      label="First Name"
                      rules={[{ required: true, message: 'Please enter your first name' }]}
                    >
                      <Input
                        placeholder="Enter first name"
                        prefix={<UserOutlined />}
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="last_name"
                      label="Last Name"
                      rules={[{ required: true, message: 'Please enter your last name' }]}
                    >
                      <Input
                        placeholder="Enter last name"
                        prefix={<UserOutlined />}
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="email"
                  label="Email Address"
                  help="Email cannot be changed for Super Admin accounts"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email address' },
                  ]}
                >
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    prefix={<MailOutlined />}
                    size="large"
                    autoComplete="off"
                    disabled
                    style={{ cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>

                <Form.Item
                  name="username"
                  label="Username"
                  rules={[{ required: true, message: 'Please enter your username' }]}
                >
                  <Input
                    placeholder="Enter username"
                    prefix={<UserOutlined />}
                    size="large"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                  <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={() => {
                      setUpdateProfileModalVisible(false);
                      loadUserProfile(); // Reload to reset form
                    }}>
                      Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Update Profile
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Modal>

            {/* 2FA Setup Modal */}
            <Modal
              title={
                <Space>
                  <SafetyOutlined />
                  <span>Set Up Two-Factor Authentication</span>
                </Space>
              }
              open={twoFactorSetupModalVisible}
              onCancel={() => {
                setTwoFactorSetupModalVisible(false);
                setQrCodeUrl('');
                setSecretKey('');
              }}
              footer={null}
              width={600}
            >
              <div style={{ padding: '20px 0' }}>
                <Paragraph>
                  To enable two-factor authentication, scan the QR code below with an authenticator app 
                  (like Google Authenticator, Authy, or Microsoft Authenticator).
                </Paragraph>

                {loading ? (
                  <div style={{ textAlign: 'center', margin: '40px 0' }}>
                    <div>Generating QR code...</div>
                  </div>
                ) : qrCodeUrl ? (
                  <>
                    <div style={{ textAlign: 'center', margin: '24px 0' }}>
                      <div style={{ 
                        display: 'inline-block', 
                        padding: '16px', 
                        background: '#fff', 
                        borderRadius: '8px',
                        border: '1px solid #d9d9d9'
                      }}>
                        <img 
                          src={qrCodeUrl} 
                          alt="2FA QR Code" 
                          style={{ display: 'block', margin: '0 auto' }}
                        />
                      </div>
                      
                      <div style={{ marginTop: '16px' }}>
                        <Text strong>Secret Key:</Text>
                        <div style={{ 
                          marginTop: '8px',
                          padding: '12px',
                          background: '#f5f5f5',
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px'
                        }}>
                          <span style={{ flex: 1, wordBreak: 'break-all' }}>{secretKey}</span>
                          <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              navigator.clipboard.writeText(secretKey.replace(/\s/g, ''));
                              message.success('Secret key copied to clipboard!');
                            }}
                            size="small"
                          />
                        </div>
                      </div>

                      <Paragraph type="secondary" style={{ marginTop: '16px', fontSize: '12px' }}>
                        If you can't scan the QR code, enter this secret key manually into your authenticator app.
                      </Paragraph>
                    </div>

                    <Divider />

                    <div style={{ textAlign: 'center' }}>
                      <Space>
                        <Button onClick={() => {
                          setTwoFactorSetupModalVisible(false);
                          setQrCodeUrl('');
                          setSecretKey('');
                        }}>
                          Cancel
                        </Button>
                        <Button 
                          type="primary" 
                          onClick={handleSetup2FA}
                        >
                          I've Scanned the QR Code
                        </Button>
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', margin: '40px 0' }}>
                    <Text type="secondary">Loading QR code...</Text>
                  </div>
                )}
              </div>
            </Modal>

            {/* 2FA Verification Modal */}
            <Modal
              title={
                <Space>
                  <SafetyOutlined />
                  <span>Verify Two-Factor Authentication</span>
                </Space>
              }
              open={twoFactorVerifyModalVisible}
              onCancel={() => {
                setTwoFactorVerifyModalVisible(false);
                twoFactorVerifyForm.resetFields();
                setQrCodeUrl('');
                setSecretKey('');
              }}
              footer={null}
              width={500}
            >
              <div style={{ padding: '20px 0' }}>
                <Paragraph>
                  Enter the 6-digit code from your authenticator app to complete the setup.
                </Paragraph>

                <Form
                  form={twoFactorVerifyForm}
                  layout="vertical"
                  onFinish={handleVerify2FA}
                  autoComplete="off"
                >
                  <Form.Item
                    name="verificationCode"
                    label="Verification Code"
                    rules={[
                      { required: true, message: 'Please enter the verification code' },
                      { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit code' },
                    ]}
                  >
                    <Input
                      placeholder="000000"
                      maxLength={6}
                      size="large"
                      style={{ 
                        fontSize: '24px', 
                        letterSpacing: '8px',
                        textAlign: 'center',
                        fontFamily: 'monospace'
                      }}
                      autoFocus
                    />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                      <Button 
                        onClick={() => {
                          setTwoFactorVerifyModalVisible(false);
                          twoFactorVerifyForm.resetFields();
                          setQrCodeUrl('');
                          setSecretKey('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Verify & Enable
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </div>
            </Modal>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default SuperAdminSettings;
